import { useEffect, useMemo, useState } from "react";
import { getFirestore } from "store/getFirebase";
import { useCollectionData, useDocumentData, usePermissions } from "hooks";
import { collection, query, where, doc } from "firebase/firestore";
import { createRacingEventsConverter } from "./useRace";
import type { RaceEventType } from "./RacingTypes";
import {
  type RaceMeetingType,
  createRaceMeetingsConverter,
} from "sections/Betting/Race/hooks/useRaceMeetings";
import { useCampaigns } from "hooks/firestore/v2/user/useCampaigns";
import type { Maybe } from "types/utilities";
import { useTokens } from "hooks/firestore/v2/user/useTokens";

export const useRaceMeeting = (
  meetingId?: string,
): [Maybe<RaceMeetingType>, Maybe<RaceEventType[]>, boolean] => {
  const db = getFirestore();
  const permissions = usePermissions();

  // this tuple is probably gross. I don't like it but solves the empty state
  // flickering on initial load by forcing the manually managed loading state to
  // be in the same state update and not having to wait the extra render
  const [[raceMeeting, raceMeetingLoading], setRaceMeeting] = useState<
    [Maybe<RaceMeetingType>, boolean]
  >([undefined, true]);

  const { codes: userCampaigns, isLoading: isCampaignsLoading } =
    useCampaigns();
  const { tokens, isLoading: isTokensLoading } = useTokens();

  const raceMeetingsConverter = useMemo(
    () => createRaceMeetingsConverter(userCampaigns, tokens, permissions),
    [userCampaigns, tokens, permissions],
  );
  const raceEventsConverter = useMemo(
    () => createRacingEventsConverter(userCampaigns, tokens, permissions),
    [userCampaigns, tokens, permissions],
  );

  const raceMeetingsRef = useMemo(() => {
    if (!meetingId || isCampaignsLoading) return null;

    return doc(db, "raceMeetings", meetingId).withConverter(
      raceMeetingsConverter,
    );
  }, [meetingId, userCampaigns, isCampaignsLoading]);

  const racesEventsRef = useMemo(() => {
    if (!raceMeetingsRef || !meetingId) return null;

    return collection(db, "racingEvents").withConverter(raceEventsConverter);
  }, [meetingId, userCampaigns, raceMeetingsRef]);

  const compiledQuery = useMemo(() => {
    if (!raceMeetingsRef || !racesEventsRef) return null;

    return query(
      racesEventsRef,
      where("meetingId", "==", meetingId),
      where("visible", "==", true),
    );
  }, [racesEventsRef, raceMeetingsRef, userCampaigns]);

  const [raceMeetingRaw, raceMeetingLoadingRaw] =
    useDocumentData(raceMeetingsRef);
  const [races, racesLoading] = useCollectionData(
    compiledQuery,
    racesEventsRef?.path,
  );

  useEffect(() => {
    if (
      !raceMeetingRaw &&
      (raceMeetingLoadingRaw ||
        racesLoading ||
        isCampaignsLoading ||
        isTokensLoading)
    ) {
      setRaceMeeting([undefined, true]);
      return;
    }

    setRaceMeeting([raceMeetingRaw, false]);
  }, [
    races,
    racesLoading,
    raceMeetingRaw,
    raceMeetingLoadingRaw,
    isCampaignsLoading,
    isTokensLoading,
  ]);

  return [
    raceMeeting,
    races?.sort((a, b) => a.number - b.number),
    raceMeetingLoading,
  ];
};
